import { apiClient } from './client'

// Enhanced Trip interface
export interface Trip {
  id: string
  institutionId: string
  routeId: string
  vehicleId: string
  primaryDriverId: string
  backupDriverId?: string
  tripName: string
  tripType: 'pickup' | 'dropoff' | 'field_trip' | 'emergency' | 'custom'
  startTime: string // HH:MM format
  endTime: string   // HH:MM format
  operatingDays: ('monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday')[]
  enrolledStudents: {
    studentId: string
    stopId: string
    emergencyContact?: {
      name: string
      phone: string
      relationship: string
    }
    specialRequirements?: string
    enrolledAt: string
    isActive: boolean
  }[]
  capacity: number
  currentEnrollment: number
  autoCreateSessions: boolean
  requiresConfirmation: boolean
  trackingEnabled: boolean
  parentNotifications: boolean
  status: 'active' | 'inactive' | 'suspended' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'delayed'
  notes?: string
  isActive: boolean
  createdById: string
  createdAt: string
  updatedAt: string
  // Populated fields
  route?: {
    id: string
    routeName: string
    routeNumber: string
  }
  vehicle?: {
    id: string
    vehicleNumber: string
    make: string
    vehicleModel: string
  }
  primaryDriver?: {
    id: string
    firstName: string
    lastName: string
    phone: string
  }
  backupDriver?: {
    id: string
    firstName: string
    lastName: string
    phone: string
  }
  // Enhanced computed fields
  studentCount?: number
  availableCapacity?: number
  operatingToday?: boolean
}

// Trip Session interface
export interface TripSession {
  id: string
  institutionId: string
  tripId: string
  sessionName: string
  startTime: string
  endTime?: string
  actualStartTime?: string
  actualEndTime?: string
  status: 'scheduled' | 'active' | 'completed' | 'cancelled'
  studentAttendance: {
    studentId: string
    stopId: string
    boardedTime?: string
    alightedTime?: string
    status: 'waiting' | 'boarded' | 'missed' | 'picked_up' | 'dropped_off'
    parentNotified?: boolean
  }[]
  driverNotes?: string
  incidents?: {
    type: 'breakdown' | 'accident' | 'delay' | 'student_issue' | 'emergency' | 'other'
    description: string
    timestamp: string
    resolved: boolean
    reportedBy: string
  }[]
  createdAt: string
  updatedAt: string
  // Populated fields
  trip?: {
    tripName: string
    tripType: string
    vehicleId: string
    primaryDriverId: string
  }
}

// Transport Settings interface
export interface TransportSettings {
  enabled: boolean
  requireStudentSelection: boolean
  autoCreateSessions: boolean
  defaultPickupTime: string
  defaultDropoffTime: string
  allowMultipleTripsPerDay: boolean
  trackingEnabled: boolean
  parentNotifications: boolean
  emergencyContacts: string[]
}

// Enhanced Trip Creation Request
export interface CreateEnhancedTripRequest {
  routeId: string
  vehicleId: string
  primaryDriverId: string
  backupDriverId?: string
  tripName: string
  tripType: 'pickup' | 'dropoff' | 'field_trip' | 'emergency' | 'custom'
  startTime: string // HH:MM format
  endTime: string   // HH:MM format
  operatingDays: string[] // Days of week the trip operates (required)
  capacity: number
  selectedStudents: string[]
  autoCreateSessions?: boolean
  institutionId?: string // For super admins
}


export interface GetTripsParams {
  page?: number
  limit?: number
  search?: string
  status?: string
  tripType?: string
  routeId?: string
  vehicleId?: string
  driverId?: string
  tripDate?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  institutionId?: string // For super admins
}

export interface TripStats {
  totalTrips: number
  todaysTrips: number
  liveTrips: number
  completedTrips: number
  scheduled: number
  inProgress: number
  completed: number
  cancelled: number
  delayed: number
}

// Get all trips with optional filtering and pagination
export const getTrips = async (params: GetTripsParams = {}) => {
  const searchParams = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value.toString())
    }
  })

  const response = await apiClient.get(`/trips?${searchParams.toString()}`)
  return response.data
}

// Get single trip by ID
export const getTripById = async (id: string) => {
  const response = await apiClient.get(`/trips/${id}`)
  return response.data
}

// Update trip request type
export interface UpdateTripRequest {
  tripName?: string
  tripType?: 'pickup' | 'dropoff' | 'field_trip' | 'emergency' | 'custom'
  routeId?: string
  vehicleId?: string
  primaryDriverId?: string
  backupDriverId?: string
  startTime?: string
  endTime?: string
  operatingDays?: string[]
  capacity?: number
  autoCreateSessions?: boolean
  requiresConfirmation?: boolean
  trackingEnabled?: boolean
  parentNotifications?: boolean
  status?: 'active' | 'inactive' | 'suspended'
  notes?: string
}

// Update trip
export const updateTrip = async (id: string, data: UpdateTripRequest) => {
  const response = await apiClient.put(`/trips/${id}`, data)
  return response.data
}

// Delete trip
export const deleteTrip = async (id: string) => {
  const response = await apiClient.delete(`/trips/${id}`)
  return response.data
}

// Start a trip (for drivers)
export const startTrip = async (id: string) => {
  const response = await apiClient.post(`/trips/${id}/start`)
  return response.data
}

// Complete a trip (for drivers)
export const completeTrip = async (id: string, notes?: string) => {
  const response = await apiClient.post(`/trips/${id}/complete`, { notes })
  return response.data
}

// Update trip location (for GPS tracking)
export const updateTripLocation = async (id: string, location: {
  latitude: number
  longitude: number
  speed?: number
}) => {
  const response = await apiClient.put(`/trips/${id}/location`, location)
  return response.data
}

// Get trip statistics
export const getTripStats = async (institutionId?: string) => {
  const params = institutionId ? `?institutionId=${institutionId}` : ''
  const response = await apiClient.get(`/trips/stats${params}`)
  return response.data
}

// Helper functions for trip management
export const getTodaysTrips = async (institutionId?: string) => {
  const today = new Date().toISOString().split('T')[0]
  return getTrips({
    tripDate: today,
    institutionId,
    sortBy: 'scheduledStartTime',
    sortOrder: 'asc'
  })
}

export const getLiveTrips = async (institutionId?: string) => {
  return getTrips({
    status: 'in_progress',
    institutionId,
    sortBy: 'actualStartTime',
    sortOrder: 'asc'
  })
}

export const getUpcomingTrips = async (institutionId?: string) => {
  return getTrips({
    status: 'active',
    institutionId,
    sortBy: 'startTime',
    sortOrder: 'asc',
    limit: 10
  })
}

// =============================================
// ENHANCED TRIP MANAGEMENT API METHODS
// =============================================

// Create enhanced trip with student selection
export const createEnhancedTrip = async (tripData: CreateEnhancedTripRequest) => {
  const response = await apiClient.post('/trips', tripData)
  return response.data
}

// Get transport settings
export const getTransportSettings = async (institutionId?: string) => {
  const params = institutionId ? `?institutionId=${institutionId}` : ''
  const response = await apiClient.get(`/trips/transport-settings${params}`)
  return response.data
}

// Get all data needed for trip form in one call (reduces multiple API calls)
export interface TripFormData {
  transportSettings: TransportSettings
  routes: {
    id: string
    routeName: string
    routeNumber: string
    stops: any[]
    estimatedDuration?: number
    schedule?: any
    assignedVehicleIds?: string[]
    assignedDriverIds?: string[]
  }[]
  vehicles: {
    id: string
    vehicleNumber: string
    make: string
    vehicleModel: string
    capacity: number
    status: string
    licensePlate?: string
  }[]
  drivers: {
    id: string
    firstName: string
    lastName: string
    email: string
    phone?: string
    profileImage?: string
  }[]
  students: {
    id: string
    firstName: string
    lastName: string
    admissionNumber?: string
    grade?: string
    profileImage?: string
  }[]
}

export const getTripFormData = async (institutionId?: string): Promise<{ success: boolean; data: TripFormData }> => {
  const params = institutionId ? `?institutionId=${institutionId}` : ''
  const response = await apiClient.get(`/trips/form-data${params}`)
  return response.data
}

// Update transport settings
export const updateTransportSettings = async (settings: Partial<TransportSettings>, institutionId?: string) => {
  const data = institutionId ? { ...settings, institutionId } : settings
  const response = await apiClient.put('/trips/transport-settings', data)
  return response.data
}

// Trip Sessions API
export interface GetTripSessionsParams {
  page?: number
  limit?: number
  tripId?: string
  status?: 'scheduled' | 'active' | 'completed' | 'cancelled'
  startDate?: string
  endDate?: string
  driverId?: string
  institutionId?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

export const getTripSessions = async (params: GetTripSessionsParams = {}) => {
  const searchParams = new URLSearchParams()
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value.toString())
    }
  })
  
  const response = await apiClient.get(`/trips/sessions?${searchParams.toString()}`)
  return response.data
}

// Start trip session (for drivers)
export const startTripSession = async (sessionId: string) => {
  const response = await apiClient.post(`/trips/sessions/${sessionId}/start`)
  return response.data
}

// End trip session (for drivers)
export const endTripSession = async (sessionId: string, notes?: string) => {
  const data = notes ? { notes } : {}
  const response = await apiClient.post(`/trips/sessions/${sessionId}/end`, data)
  return response.data
}

// Student enrollment in trips
export const enrollStudentInTrip = async (tripId: string, studentId: string, stopId: string) => {
  const response = await apiClient.post(`/trips/${tripId}/enroll`, {
    studentId,
    stopId
  })
  return response.data
}

// Unenroll student from trip
export const unenrollStudentFromTrip = async (tripId: string, studentId: string) => {
  const response = await apiClient.delete(`/trips/${tripId}/unenroll/${studentId}`)
  return response.data
}

// Helper functions for enhanced trip management
export const getTodaysTripSessions = async (institutionId?: string) => {
  const today = new Date().toISOString().split('T')[0]
  return getTripSessions({
    startDate: today,
    endDate: today,
    institutionId,
    sortBy: 'startTime',
    sortOrder: 'asc'
  })
}

export const getActiveTripSessions = async (institutionId?: string) => {
  return getTripSessions({
    status: 'active',
    institutionId,
    sortBy: 'actualStartTime',
    sortOrder: 'asc'
  })
}

export const getDriverTripSessions = async (driverId: string, institutionId?: string) => {
  return getTripSessions({
    driverId,
    institutionId,
    sortBy: 'startTime',
    sortOrder: 'desc',
    limit: 20
  })
}